import * as types from './ActionTypes';
import _ from 'lodash';
import { create } from 'axios';
import { applyPeopleFilter } from './filter.action';
import { addToast } from './toast.action';
import { sendRegistrationEmail } from './registrationEmail.action';

const ajax = create({
  baseURL: process.env.REACT_APP_CORE_USER_API_URL,
  contentType: 'application/json',
  withCredentials: true
});


export const getUsers = () => dispatch => {
  dispatch({ type: types.GET_TEAM_PENDING });

  ajax.get('/persons?attachRelationships=true&activeOnly=false')
    .then(resp => {
      const formattedUsers = resp.data.persons.map(x => {
        if (x.relationships) {
          x.role = x.relationships[0].relationshipType;
        }
        return x;
      }).filter(x => x.role !== 'GLOBAL_ADMIN');
      
      dispatch({
        type: types.GET_TEAM_SUCCESS,
        payload: formattedUsers
      });
      dispatch(applyPeopleFilter(null));
    })
    .catch(error => {
      console.log(error);
      dispatch({
        type: types.GET_TEAM_REJECTED,
        payload: error
      });
    });
};

export const createNewUser = (user, email) => dispatch => {
  dispatch({ type: types.CREATE_USER_PENDING });

  const newUser = {
    firstName: user.firstName,
    lastName: user.lastName,
    displayName: `${user.firstName} ${user.lastName}`,
    email: user.contactString,
    relationships: user.relationships.map(x => {
      return {
        fromPartyKey: x.fromPartyKey,
        relationshipType: x.relationshipType
      };
    })
  };

  if (email) {
    ajax.post('/persons', newUser)
      .then(resp => {
        return dispatch(
          sendRegistrationEmail({
            displayName: newUser.displayName,
            externalKey: resp.data.externalKey
          })
        );
      })
      .then(() => {
        dispatch(getUsers());
        dispatch(applyPeopleFilter(null));
      })
      .then(() =>
        dispatch(
          addToast({
            autohide: false,
            toasts: [
              {
                action: 'dismiss',
                text: `User: ${newUser.displayName} has been created`
              }
            ]
          })
        )
      )
      .catch(error => {
        console.log(error);
        dispatch({
          type: types.CREATE_USER_REJECTED,
          payload: error
        });
        dispatch({
          type: types.SET_ERROR_DIALOG,
          payload: {
            error,
            visible: true
          }
        });
      });
  } else {
    ajax.post('/persons', newUser)
      .then(() => {
        dispatch(applyPeopleFilter(null));
        dispatch(getUsers());
      })
      .then(() =>
        dispatch(
          addToast({
            autohide: false,
            toasts: [
              {
                action: 'dismiss',
                text: `User: ${newUser.displayName} has been created`
              }
            ]
          })
        )
      )
      .catch(error => {
        console.log(error);
        dispatch({
          type: types.CREATE_USER_REJECTED,
          payload: error
        });
        dispatch({
          type: types.SET_ERROR_DIALOG,
          payload: {
            error,
            visible: true
          }
        });
      });
  }
};

export const updateUser = data => async dispatch => {
  const editUser = data.edits;
  const originalUser = data.original;
  const userEdits = {
    firstName: editUser.firstName,
    lastName: editUser.lastName,
    displayName: `${editUser.firstName} ${editUser.lastName}`,
    email: editUser.contactString
  };

  const originalUserBody = {
    firstName: originalUser.firstName,
    lastName: originalUser.lastName,
    displayName: `${originalUser.firstName} ${originalUser.lastName}`,
    email: originalUser.contactString
  };

  const originalRelationshipState = data.edits.relationships.map(r => {
    return {
      fromPartyKey: r.fromPartyKey,
      relationshipType: r.relationshipType,
      toPartyKey: r.toPartyKey
    };
  });

  /* Build the body for the PUT /api/relationships/state endpoint that checks that
   * new state of a party's relationships is valid with respect to the rules. 
   
   * if it is the same as originalRelationshipState, we do not need to make the
   * AJAX call to PUT /api/relationships/state */
  const newRelationshipState = data.edits.relationships.map(r => {
    return {
      toPartyKey: editUser.externalKey, // same for all since editing single person.
      fromPartyKey: r.fromPartyKey,
      relationshipType: r.relationshipType
    };
  });

  try {
    dispatch({
      type: types.UPDATE_USER_PENDING
    });

    /* If the person attributes controlled by PUT person endpoint were changed,
     * make the AJAX call to change them.*/
    const personAttributesEdited =
      userEdits.displayName !== originalUserBody.displayName ||
      userEdits.email !== originalUserBody.email;

    if (personAttributesEdited) {
      await ajax.put(`/persons/${editUser.externalKey}`, userEdits);
    }

    /* If the new relationship state is different than the old relationship state
     * (e.g. the relationships were edited in the UI), make an AJAX call to
     * attempt to change them with the state change endpoint. */
    const relationshipStateEdited = !_.isEqual(
      newRelationshipState,
      originalRelationshipState
    );

    if (relationshipStateEdited) {
      await ajax.put(`/relationships/state/${editUser.externalKey}`, { newRelationshipState });
    }

    // Successful Finish
    dispatch({
      type: types.UPDATE_USER_SUCCESS
    });

    dispatch(getUsers());

    dispatch(applyPeopleFilter(null));

    dispatch(
      addToast({
        autohide: false,
        toasts: [
          {
            action: 'dismiss',
            text: `${userEdits.displayName ||
              originalUserBody.displayName} has been edited`
          }
        ]
      })
    );
  } catch (error) {
    // Unsuccessful Finish
    dispatch({
      type: types.UPDATE_USER_REJECTED,
      payload: error
    });

    dispatch({
      type: types.SET_ERROR_DIALOG,
      payload: {
        error,
        visible: true
      }
    });
  }
};

export const deactivateUser = user => dispatch => {
  dispatch({ type: types.DELETE_USER_PENDING });

  const delBody = { externalKeys: [user.externalKey] };

  ajax.delete('/persons', { data: delBody })
    .then(resp => {
      dispatch({ type: types.DELETE_USER_SUCCESS });
      return resp.data;
    })
    .then(() => {
      dispatch(getUsers());
      return true;
    })
    .then(() => {
      dispatch(
        addToast({
          autohide: false,
          toasts: [
            {
              action: 'dismiss',
              text: `${user.displayName}'s relationships have been deactivated.`
            }
          ]
        })
      );
    })
    .catch(error => {
      console.log(error);
      dispatch({
        type: types.DELETE_USER_REJECTED,
        payload: error
      });
      dispatch({
        type: types.SET_ERROR_DIALOG,
        payload: {
          error,
          visible: true
        }
      });
    });
};

export const getActiveEmail = (email) => {
  return ajax.get(`/persons/contact-methods?email=${encodeURIComponent(email)}`)
    .then(resp => resp.data.hasOwnProperty('contactMethods'));
};




// WEBPACK FOOTER //
// ./src/actions/users.action.js